// examenPropuesta.js — el alumno confirma por WhatsApp si va al examen (9/10/2026, Náyade y Poveda). Ficha:
// docs/features/autoescuela-examen-confirmacion.md
//
// Al apuntar a un alumno a un examen (o a toda la convocatoria), la autoescuela puede PREGUNTARLE si va: le llega
// examen_propuesta_crm_trial con «Sí, voy» / «No puedo». La respuesta se guarda en el examen (confirmacion "si" | "no"
// + confirmacion_at) y se ve en la ficha del alumno y en la convocatoria; «No puedo» abre además una petición en
// Planificación → Peticiones y las dos avisan en la campanita.
//
// ESPEJO del backend (lo fija __tests__/examenPropuesta.mirror.test.js):
//   - PLANTILLA_PROPUESTA          ← admin_scripts/add_autoescuela_examen_propuesta_y_bono_templates.js (lo creado en Meta)
//   - cuandoExamen                 ← aws_lambdas/c_putExamen/index.js ({{4}} de la plantilla)
//   - RESPUESTAS / cuandoCorto     ← aws_lambdas/c_processMessage/examenBotones.js (lo que se le contesta al pulsar)
//
// PURO y sin imports. COPIA POR VALOR (fichero idéntico) en
// apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/examenPropuesta.js — un test del simulador los compara.

// Las dos plantillas (es y ca) están APPROVED / UTILITY desde el 9/10/2026. A false, la opción se ve pero avisa de que
// aún no sale.
export const EXAMEN_PROPUESTA_DISPONIBLE = true;

export const BOTON_EXAMEN = Object.freeze({ si: "Sí, voy", no: "No puedo" });
// Etiquetas en catalán → la de castellano (c_sendTemplate pone la `es` como payload: el backend las trata igual).
export const BOTON_EXAMEN_CA = Object.freeze({ "Sí, hi aniré": "Sí, voy", "No puc": "No puedo" });

export const PLANTILLA_PROPUESTA = {
  es: {
    body: "📩 *{{1}}* te informa:\n\nHola {{2}}, te hemos propuesto para el examen {{3}} del {{4}}.\n\nConfirma con los botones si podrás asistir. Si no puedes, la autoescuela buscará otra fecha contigo.",
    buttons: ["Sí, voy", "No puedo"],
  },
  ca: {
    body: "📩 *{{1}}* t'informa:\n\nHola {{2}}, t'hem proposat per a l'examen {{3}} del {{4}}.\n\nConfirma amb els botons si hi podràs assistir. Si no pots, l'autoescola buscarà una altra data amb tu.",
    buttons: ["Sí, hi aniré", "No puc"],
  },
};

const DIAS = {
  es: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
  ca: ["diumenge", "dilluns", "dimarts", "dimecres", "dijous", "divendres", "dissabte"],
};
const L = (lang) => (String(lang || "").toLowerCase() === "ca" ? "ca" : "es");
const partes = (fecha) => /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha || ""));
const dowDe = (m) => new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();

/** {{4}} de la plantilla: «martes 20/10/2026 a las 10:00» (c_putExamen.cuandoExamen). */
export function cuandoExamen(examen, lang) {
  const m = partes(examen && examen.fecha);
  if (!m) return String((examen && examen.fecha) || "");
  const l = L(lang);
  const hora = examen.hora ? (l === "ca" ? ` a les ${examen.hora}` : ` a las ${examen.hora}`) : "";
  return `${DIAS[l][dowDe(m)]} ${m[3]}/${m[2]}/${m[1]}${hora}`;
}

/** «martes 20/10 a las 10:00» (examenBotones.cuandoCorto: la respuesta al alumno y la nota de la petición). */
export function cuandoCorto(examen, lang) {
  const m = partes(examen && examen.fecha);
  if (!m) return "";
  const l = L(lang);
  return `${DIAS[l][dowDe(m)]} ${Number(m[3])}/${Number(m[2])}${examen.hora ? ` ${l === "ca" ? "a les" : "a las"} ${examen.hora}` : ""}`;
}

export const tipoExamen = (tipo, lang) => (L(lang) === "ca" ? (tipo === "practico" ? "pràctic" : "teòric") : (tipo === "practico" ? "práctico" : "teórico"));

/** Lo que le contesta c_processMessage/examenBotones.js al pulsar (texto libre, sin «¡»/«¿» de apertura). */
export const RESPUESTAS = {
  es: {
    si: (n, t, c) => `Perfecto${n ? `, ${n}` : ""}! Queda anotado que vas al examen ${t}${c ? ` del ${c}` : ""}. Mucha suerte!`,
    no: (n) => `Entendido${n ? `, ${n}` : ""}. Se lo hemos dicho a la autoescuela para buscarte otra fecha. Te escribirán por aquí.`,
  },
  ca: {
    si: (n, t, c) => `Perfecte${n ? `, ${n}` : ""}! Queda anotat que vas a l'examen ${t}${c ? ` del ${c}` : ""}. Molta sort!`,
    no: (n) => `D'acord${n ? `, ${n}` : ""}. Ho hem dit a l'autoescola perquè et busqui una altra data. T'escriuran per aquí.`,
  },
};

/** El texto de la respuesta para un botón ("Sí, voy" / "No puedo", o su etiqueta en catalán). null si no es suyo. */
export function respuestaExamen(boton, { nombre, examen, lang } = {}) {
  const b = BOTON_EXAMEN_CA[boton] || boton;
  const l = L(lang);
  const n = String(nombre || "").trim().split(/\s+/)[0] || "";
  if (b === BOTON_EXAMEN.si) return RESPUESTAS[l].si(n, tipoExamen(examen && examen.tipo, l), cuandoCorto(examen, l));
  if (b === BOTON_EXAMEN.no) return RESPUESTAS[l].no(n);
  return null;
}

export const esBotonExamen = (boton) => Object.values(BOTON_EXAMEN).includes(BOTON_EXAMEN_CA[boton] || boton);

/** La plantilla rellenada (vista previa): { message, buttons }. */
export function mensajePropuesta({ conext = true, negocio, nombre, examen, lang } = {}) {
  const l = conext ? L(lang) : "es";
  const p = PLANTILLA_PROPUESTA[l];
  const vals = [negocio || (l === "ca" ? "La teva autoescola" : "Tu autoescuela"), nombre || (l === "ca" ? "Alumne" : "Alumno"), tipoExamen(examen && examen.tipo, l), cuandoExamen(examen, l)];
  let message = p.body.replace(/\{\{(\d)\}\}/g, (_, i) => vals[Number(i) - 1]);
  // Con número propio no hay cabecera de negocio (no se usa: todas las autoescuelas van por el compartido).
  if (!conext) message = message.replace(/^📩 \*[^*]*\* te informa:\n\n/, "");
  return { message, buttons: [...p.buttons] };
}

/**
 * Estado de la respuesta de un examen: "si" | "no" | "sin_respuesta" (preguntado, sin contestar) | null (no se le
 * ha preguntado). Solo cuenta mientras el examen está pendiente.
 */
export function estadoConfirmacion(examen) {
  if (!examen) return null;
  if (examen.confirmacion === "si" || examen.confirmacion === "no") return examen.confirmacion;
  if (examen.propuesta_enviada_at && (examen.status || "pendiente") === "pendiente") return "sin_respuesta";
  return null;
}

/** Recuento de la convocatoria: { si, no, sin_respuesta, preguntados }. */
export function resumenConvocatoria(examenes) {
  const r = { si: 0, no: 0, sin_respuesta: 0, preguntados: 0 };
  for (const e of Array.isArray(examenes) ? examenes : []) {
    const s = estadoConfirmacion(e);
    if (!s) continue;
    r[s] += 1;
    r.preguntados += 1;
  }
  return r;
}

/** A quién se le pregunta desde la convocatoria: pendientes a los que aún no se les ha preguntado. */
export const porPreguntar = (examenes) => (Array.isArray(examenes) ? examenes : [])
  .filter((e) => (e.status || "pendiente") === "pendiente" && !estadoConfirmacion(e));

/** ¿Es la petición «No puede ir al examen» (c_processMessage/examenBotones.js)? */
export const esExamenNoPuede = (peticion) => !!peticion && peticion.kind === "examen_no_puede";
